import axios from 'axios';

import { apiInterceptor } from './axios';
import { API_URL } from 'shared/api/Constants';
import { isImageUploadFile } from 'utils/imageUtils';
import { prepareImageDocumentUpload } from 'utils/pdfUtils';

export const checkDocumentStatusAPI = (patientId: number) => {
  return apiInterceptor
    .get(`${API_URL.CHECK_DOCUMENT_STATUS}/${patientId}`)
    .then((response: any) => {
      return response;
    });
};

export const acknowledgeConsentAPI = (patientId: number) => {
  return apiInterceptor
    .post(`${API_URL.ACKNOWLEDGE_CONSENT}/${patientId}/acknowledge`)
    .then((response: any) => {
      return response;
    });
};

export const getDocumentListAPI = (patientId: number) => {
  return apiInterceptor
    .get(`${API_URL.DOCUMENT_LIST}/${patientId}`)
    .then((response: any) => {
      return response;
    });
};

export const downloadDocumentAPI = (patientId: number) => {
  return apiInterceptor
    .get(`${API_URL.DOWNLOAD_DOCUMENT}/${patientId}`)
    .then((response: any) => {
      return response;
    });
};

export interface PatientUploadedDocument {
  documentId: number;
  documentName: string;
  documentDescription?: string;
  status: string;
  uploadedDate: string;
  sourceType: string;
}

export const getPatientUploadedDocumentsAPI = (patientId: number) => {
  return apiInterceptor
    .get<PatientUploadedDocument[]>(`${API_URL.PATIENT_UPLOADED_DOCUMENTS}/${patientId}`)
    .then((response) => response.data);
};

export interface UploadPatientDocumentParams {
  patientId: string;
  documentName: string;
  documentDescription?: string;
  generatedPdf?: Blob;
  images?: Blob[];
  sourceFile?: File;
}

const getUploadAuthHeaders = () => {
  const token = sessionStorage.getItem('access-token')?.slice(1, -1);
  return {
    Authorization: `Bearer ${token}`,
    Accept: '*/*',
  };
};

const extractUploadErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    return (
      (error.response?.data as { message?: string })?.message ??
      (error.response?.data as { errorMsg?: string })?.errorMsg ??
      'Document upload failed'
    );
  }
  return 'Document upload failed';
};

const appendDocumentMetadata = (
  formData: FormData,
  {
    patientId,
    documentName,
    documentDescription,
  }: Pick<UploadPatientDocumentParams, 'patientId' | 'documentName' | 'documentDescription'>
) => {
  formData.append('patientId', patientId);
  formData.append('documentName', documentName);

  if (documentDescription) {
    formData.append('documentDescription', documentDescription);
  }
};

const getImageUploadFilename = (image: Blob, index: number) => {
  if (image instanceof File && image.name) {
    return image.name;
  }

  return `page-${index + 1}.jpg`;
};

const buildImageUploadFormData = ({
  patientId,
  documentName,
  documentDescription,
  generatedPdf,
  images,
}: Required<
  Pick<
    UploadPatientDocumentParams,
    'patientId' | 'documentName' | 'generatedPdf' | 'images'
  >
> &
  Pick<UploadPatientDocumentParams, 'documentDescription'>) => {
  const formData = new FormData();
  appendDocumentMetadata(formData, { patientId, documentName, documentDescription });

  const pdfName = `${documentName.replace(/\s+/g, '_') || 'document'}.pdf`;
  formData.append('generatedPdf', generatedPdf, pdfName);
  images.forEach((image, index) => {
    formData.append('images', image, getImageUploadFilename(image, index));
  });

  return formData;
};

const uploadDocumentPdf = async (formData: FormData) => {
  const response = await axios.post(API_URL.UPLOAD_DOCUMENT_PDF, formData, {
    headers: getUploadAuthHeaders(),
  });
  return response.data;
};

const uploadDocumentImages = async (formData: FormData) => {
  const response = await axios.post(API_URL.UPLOAD_DOCUMENT_IMAGES, formData, {
    headers: getUploadAuthHeaders(),
  });
  return response.data;
};

export const uploadPatientDocumentAPI = async ({
  patientId,
  documentName,
  documentDescription,
  generatedPdf,
  images,
  sourceFile,
}: UploadPatientDocumentParams) => {
  try {
    if (sourceFile) {
      if (isImageUploadFile(sourceFile)) {
        const imageUploadPayload = await prepareImageDocumentUpload([sourceFile]);
        const formData = buildImageUploadFormData({
          patientId,
          documentName,
          documentDescription,
          generatedPdf: imageUploadPayload.generatedPdf,
          images: imageUploadPayload.images,
        });
        return uploadDocumentImages(formData);
      }

      const formData = new FormData();
      appendDocumentMetadata(formData, { patientId, documentName, documentDescription });
      formData.append('document', sourceFile, sourceFile.name);
      return uploadDocumentPdf(formData);
    }

    if (generatedPdf && images && images.length > 0) {
      const formData = buildImageUploadFormData({
        patientId,
        documentName,
        documentDescription,
        generatedPdf,
        images,
      });
      return uploadDocumentImages(formData);
    }

    throw new Error('Invalid upload payload.');
  } catch (error) {
    throw new Error(extractUploadErrorMessage(error));
  }
};

const STAFF_ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.pdf', '.doc', '.docx'];

const STAFF_ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export const isStaffAcceptedUploadFile = (file: File): boolean => {
  const extension = file.name.includes('.')
    ? file.name.slice(file.name.lastIndexOf('.')).toLowerCase()
    : '';

  if (STAFF_ALLOWED_EXTENSIONS.includes(extension)) {
    return true;
  }

  return STAFF_ALLOWED_MIME_TYPES.includes(file.type);
};

export const STAFF_UPLOAD_ACCEPT_ATTR =
  '.jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,image/jpeg,image/png,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export interface UploadStaffDocumentParams {
  patientId: string;
  documentName: string;
  documentDescription?: string;
  sourceFile: File;
}

export const uploadStaffDocumentAPI = async ({
  patientId,
  documentName,
  documentDescription,
  sourceFile,
}: UploadStaffDocumentParams) => {
  try {
    const formData = new FormData();
    formData.append('patientId', patientId);
    formData.append('documentName', documentName);
    if (documentDescription) {
      formData.append('documentDescription', documentDescription);
    }
    formData.append('document', sourceFile, sourceFile.name);

    const response = await axios.post(API_URL.UPLOAD_STAFF_DOCUMENT, formData, {
      headers: getUploadAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(extractUploadErrorMessage(error));
  }
};

export const getStaffUploadedDocumentsAPI = (patientId: number) => {
  return apiInterceptor
    .get<PatientUploadedDocument[]>(`${API_URL.STAFF_UPLOADED_DOCUMENTS}/${patientId}`)
    .then((response) => response.data);
};

export const getGuardianUploadedDocumentsAPI = (patientId: number) => {
  return apiInterceptor
    .get<PatientUploadedDocument[]>(`${API_URL.GUARDIAN_UPLOADED_DOCUMENTS}/${patientId}`)
    .then((response) => response.data);
};

export const downloadUploadedDocumentAPI = async (
  patientId: number,
  documentId: number,
  fileName: string
) => {
  const response = await axios.get<Blob>(
    `${API_URL.DOCUMENT_FILE_DOWNLOAD}/${documentId}/download`,
    {
      params: { patientId },
      responseType: 'blob',
      headers: getUploadAuthHeaders(),
    }
  );

  const contentType = response.headers['content-type'] ?? 'application/octet-stream';
  const blob = new Blob([response.data], { type: contentType });
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(blobUrl);
};
